import { useCallback } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { FundRawRecord } from '@/components/funds-raw/types';

interface UseCompanyFundsActionsParams {
  fundRecords: FundRawRecord[];
  openFundsRaw: (opts: { fund?: FundRawRecord | null; onSuccess?: () => void }) => void;
  externalAddToPortfolio?: (fundId: string) => void;
  externalFavorite?: (fundId: string) => void;
  externalAddToWatchlist?: (fundId: string) => void;
  refetchFundsForCompany?: () => void;
}

export function useCompanyFundsActions({
  fundRecords,
  openFundsRaw,
  externalAddToPortfolio,
  externalFavorite,
  externalAddToWatchlist,
  refetchFundsForCompany,
}: UseCompanyFundsActionsParams) {
  const { toast } = useToast();

  const handleAddToPortfolio = useCallback((fundId: string, directFund?: FundRawRecord) => {
    try {
      console.log('AddToPortfolio called with ID:', fundId);
      console.log('AddToPortfolio received direct fund:', directFund);
      console.log('Available fund records:', fundRecords.length);

      const directFundHasOnlyId = directFund && (Object.keys(directFund)?.length || 0) === 1 && Object.keys(directFund)[0] === 'id';
      if (directFundHasOnlyId) {
        console.log('Received fund with only ID, will try to find full details');
      }

      const fullFundFromRecords = fundRecords.find((f) => f.id === fundId);
      console.log('Found fund in records?', !!fullFundFromRecords);

      const fundToUse = (fullFundFromRecords || directFund) as FundRawRecord | undefined;

      if (fundToUse) {
        const fundWithDisplayName = {
          ...fundToUse,
          fund_name:
            fundToUse.fund_name || `${fundToUse.management_company_code || ''} ${fundToUse.fund_code || ''}`.trim() || 'Unknown fund',
          display_name:
            fundToUse.fund_name || `${fundToUse.management_company_code || ''} ${fundToUse.fund_code || ''}`.trim() || 'Unknown fund',
        } as FundRawRecord;

        openFundsRaw({ fund: fundWithDisplayName });
      } else {
        console.error('Could not find fund with ID:', fundId);
        toast({ title: 'Error', description: 'Could not load fund details', variant: 'destructive' });
      }

      if (externalAddToPortfolio) {
        externalAddToPortfolio(fundId);
      }
    } catch (error) {
      console.error('Error opening add to portfolio dialog:', error);
      toast({ title: 'Error', description: 'Failed to open portfolio selection', variant: 'destructive' });
    }
  }, [fundRecords, openFundsRaw, externalAddToPortfolio, toast]);

  const handleFavorite = useCallback((fundId: string) => {
    toast({ title: 'Success', description: 'Fund added to favorites', variant: 'default' });
    if (externalFavorite) externalFavorite(fundId);
    if (refetchFundsForCompany) refetchFundsForCompany();
  }, [externalFavorite, refetchFundsForCompany, toast]);

  const handleWatchlist = useCallback((fundId: string) => {
    toast({ title: 'Success', description: 'Fund added to watchlist', variant: 'default' });
    if (externalAddToWatchlist) externalAddToWatchlist(fundId);
    if (refetchFundsForCompany) refetchFundsForCompany();
  }, [externalAddToWatchlist, refetchFundsForCompany, toast]);

  return { handleAddToPortfolio, handleFavorite, handleWatchlist };
}
